import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Shown to a new account that hasn't put a card on file yet. Hard-walls the app but lets
// the user complete checkout (which starts the 14-day trial) or sign out.
const PLANS = [
  { id: 'free', name: 'Personal', price: '$7/mo', storage: '100 GB' },
  { id: 'pro', name: 'Pro', price: '$39/mo', storage: '500 GB' },
  { id: 'studio', name: 'Studio', price: '$99/mo', storage: '2 TB' },
  { id: 'business', name: 'Business', price: '$179/mo', storage: '5 TB' },
];

export default function Paywall() {
  const { logout } = useAuth();
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function startCheckout() {
    if (!plan) { setError('Please choose a plan.'); return; }
    setError(null);
    setLoading(true);
    try {
      const promoCode = (() => { try { return localStorage.getItem('promoCode') || undefined; } catch { return undefined; } })();
      const res = await fetch('/api/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier: plan, source: 'paywall', promoCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.checkoutUrl && data.checkoutUrl !== '#') {
        try { localStorage.removeItem('promoCode'); } catch {}
        window.location.href = data.checkoutUrl;
        return;
      }
      setError('Could not start checkout. Please try again or contact support.');
    } catch (err) {
      console.error('Paywall checkout failed:', err);
      setError('Could not start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 max-w-md w-full p-8">
        <div className="flex justify-center mb-4">
          <img src="/favicon/favicon-96x96.png" alt="Photo Harbor" className="h-12 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-1">Activate your account</h1>
        <p className="text-center text-gray-600 mb-6">
          Add a payment method to start using Photo Harbor. You won't be charged during your
          14-day free trial, and you can cancel anytime.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">Choose a plan</label>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          disabled={loading}
          required
          className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm"
        >
          <option value="" disabled>Select a plan</option>
          {PLANS.map((p) => (
            <option key={p.id} value={p.id}>{p.name}, {p.price} ({p.storage})</option>
          ))}
        </select>

        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

        <button
          onClick={startCheckout}
          disabled={loading || !plan}
          className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? 'Starting…' : 'Add payment method & start free trial'}
        </button>

        <button
          onClick={() => logout && logout()}
          disabled={loading}
          className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
